import React, { useCallback, useState } from "react";

import { Link } from "react-router-dom";

import { makePatch } from "@culturehq/client";
import { classnames, ClickClose, Icon, PlainButton, Thumbnail } from "@culturehq/components";

import { useProfile } from "app/context";
import { useIncrement } from "app/hooks";
import { Trans, t } from "app/utils";

import Feedback from "components/Feedback";
import Accordian from "components/common/Accordian";
import InviteModal from "components/common/InviteModal";
import OrganizationRow from "components/common/OrganizationRow";

import getUserName from "app/utils/getUserName";
import DropdownItem from "./DropdownItem";

const combineFns = (...fns) => () => fns.forEach(fn => fn());

const InviteUsersItem = ({ onClick }) => (
  <li>
    <InviteModal>
      {onTrigger => (
        <PlainButton className="chq-nav--dropi" onClick={combineFns(onClick, onTrigger)}>
          <Trans>Invite New Users</Trans>
        </PlainButton>
      )}
    </InviteModal>
  </li>
);

const FeedbackItem = ({ onClick }) => {
  const [key, onIncrementKey] = useIncrement(1);

  return (
    <li>
      <Feedback key={key} onClose={onIncrementKey}>
        {onTrigger => (
          <PlainButton className="chq-nav--dropi" onClick={combineFns(onClick, onTrigger)}>
            <Trans>Culture Suggestion Box</Trans>
          </PlainButton>
        )}
      </Feedback>
    </li>
  );
};

const ManageUsersItem = ({ onClick }) => (
  <>
    <hr />
    <DropdownItem to="/admin/users" onClick={onClick}>
      <Trans>Manage Users</Trans>
    </DropdownItem>
  </>
);

const HelpCenterItem = ({ onClick }) => (
  <li>
    <a
      className="chq-nav--dropi"
      target="_blank"
      rel="noopener noreferrer"
      href="https://intercom.help/culturehq"
      onClick={onClick}
    >
      <Trans>Help Center</Trans>{" "}
      <Icon icon="android-open" />
    </a>
  </li>
);

const Profile = () => {
  const { profile } = useProfile();

  const [open, setOpen] = useState(false);

  const onToggle = useCallback(() => setOpen(value => !value), [setOpen]);
  const onClose = useCallback(
    () => {
      if (open) {
        setOpen(false);
      }
    },
    [open, setOpen]
  );

  const handleSelectOrganization = async organizationId => {
    await makePatch("/profile/current_organization", { organizationId });
    window.location.href = "/feed";
  };

  return (
    <ClickClose className="chq-nav--profile" onClose={onClose}>
      <PlainButton onClick={onToggle}>
        <Thumbnail image={profile.avatar.thumbUrl} title={getUserName(profile)} />
        {" "}
        <span className="caret" />
      </PlainButton>
      <ul className={classnames("chq-nav--drop", { "chq-nav--drop-open": open })}>
        <li className="chq-nav--welcome">
          <Thumbnail image={profile.avatar.thumbUrl} title={getUserName(profile)} />
          <p><Trans>Hi, </Trans> {getUserName(profile)}</p>
          <p>
            <Link to="/profile" onClick={onClose}>
              <Trans>View Profile</Trans>
            </Link> |{" "}
            <Link to="/profile/settings" onClick={onClose}>
              <Trans>Edit</Trans>
            </Link>
          </p>
        </li>
        {profile.organizations?.length > 1 && (
          <Accordian
            className="OrganizationsAccordian"
            organizationsAccordian
            profile={profile}
            upDown
          >
            {profile.organizations
              .filter(organization => organization.id !== profile.organization.id)
              .map(organization => (
                <OrganizationRow
                  key={organization.id}
                  onSelectOrganization={handleSelectOrganization}
                  organization={organization}
                />
              ))}
          </Accordian>
        )}
        <hr />
        <DropdownItem to="/profile/settings/social" onClick={onClose}>
          <Trans>My Social Accounts</Trans>
        </DropdownItem>
        {profile.organization.eventsEnabled && (
          <DropdownItem to="/profile/events" onClick={onClose}>
            <Trans>My Events</Trans>
          </DropdownItem>
        )}
        {profile.organization.userCreatedInvitesEnabled && (
          <InviteUsersItem onClick={onClose} />
        )}
        {profile.organization.feedbackEnabled && (
          <FeedbackItem onClick={onClose} />
        )}
        {profile.organization.accountInfoEnabled && (
          <DropdownItem to="/profile/status" onClick={onClose}>
            <Trans>Account Info</Trans>
          </DropdownItem>
        )}
        {profile.organizationAdmin && <ManageUsersItem onClick={onClose} />}
        <hr />
        <HelpCenterItem onClick={onClose} />
        <DropdownItem to="/security" onClick={onClose}>
          <Trans>Security</Trans>
        </DropdownItem>
        <DropdownItem to="/logout" onClick={onClose}>
          <Trans>Sign Out</Trans>
        </DropdownItem>
      </ul>
    </ClickClose>
  );
};

export default Profile;
